// src/pages/admin/AdminDashboard.jsx

import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  LayoutDashboard,
  Users,
  FileText,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  UserCheck,
  FileCheck,
  CreditCard,
  BarChart3,
  Shield,
Mail,
  Flag,
  Globe,
  ChevronDown,
  Check,
  UserCircle,
  Edit,
  Save,
  Camera
} from 'lucide-react';
import { useLanguage, languages } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/Dashboard.module.css';
import AdminProviderApplications from './AdminProviderApplications';
import AdminReports from './AdminReports';
import AdminDonations from './AdminDonations';
import AdminSettings from './AdminSettings';
import AdminOverview from './AdminOverview';
import AdminProfile from './AdminProfile';
import AdminMessagesPage from './AdminMessagesPage';

// Import Redux selectors
import { 
  selectIsAuthenticated,
  selectCurrentUser,
  selectAuthLoading,
  adminLogout
} from '../../store/slices/adminSlice';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  
  // Use Redux selectors instead of useAuth
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const adminUser = useSelector(selectCurrentUser);
  const authLoading = useSelector(selectAuthLoading);
  
  const { currentLanguage, changeLanguage, isTranslating } = useLanguage();
  
  // Translation hooks for dashboard
  const { translatedText: overview } = useTranslation('Overview');
  const { translatedText: applications } = useTranslation('Applications');
  const { translatedText: reports } = useTranslation('Reports');
  const { translatedText: donations } = useTranslation('Donations');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: profile } = useTranslation('Profile');
  const { translatedText: adminPanel } = useTranslation('Admin Panel');
  const { translatedText: administrator } = useTranslation('Administrator');
  const { translatedText: logoutLabel } = useTranslation('Logout');
  const { translatedText: searchPlaceholder } = useTranslation('Search...');
  const { translatedText: language } = useTranslation('Language');
  const { translatedText: selectLanguage } = useTranslation('Select Language');
  const { translatedText: currentLanguageLabel } = useTranslation('Current Language');
  const { translatedText: updatingLanguage } = useTranslation('Updating language...');
  const { translatedText: noNotifications } = useTranslation('No new notifications');
  const { translatedText: viewAll } = useTranslation('View All');

  const menuItems = [
    { path: '/admin/overview', icon: LayoutDashboard, label: overview || 'Overview' },
    { path: '/admin/applications', icon: UserCheck, label: applications || 'Applications' },
     { path: '/admin/messages', icon: Mail, label: 'Messages' }, // ← Add this
    { path: '/admin/reports', icon: Flag, label: reports || 'Reports' },
    { path: '/admin/donations', icon: DollarSign, label: donations || 'Donations' },
    { path: '/admin/profile', icon: UserCircle, label: profile || 'Profile' },
    { path: '/admin/settings', icon: Settings, label: settings || 'Settings' },
  ];

  const isActive = (path) => location.pathname === path;

  // Handle logout with Redux
  const handleLogout = async () => {
    try {
      await dispatch(adminLogout()).unwrap();
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to login
      window.location.href = '/admin/login';
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const handleLanguageSelect = async (languageCode) => {
    if (languageCode !== currentLanguage) {
      await changeLanguage(languageCode);
    }
    setShowLanguageMenu(false);
  };

  const getCurrentLanguageLabel = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? `${lang.flag} ${lang.name}` : '🇺🇸 English';
  };

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageMenu && !event.target.closest('.language-selector')) {
        setShowLanguageMenu(false);
      }
      if (showNotifications && !event.target.closest('.notification-dropdown')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageMenu, showNotifications]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
        setMobileSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get admin name with fallbacks
  const getAdminName = () => {
    if (!adminUser) return 'Admin';
    return adminUser.name || 
           adminUser.fullName || 
           adminUser.username || 
           adminUser.email?.split('@')[0] || 
           'Admin';
  };

  // Get admin avatar
  const getAdminAvatar = () => {
    const name = getAdminName();
    return name.charAt(0).toUpperCase();
  };

  // Get admin profile image URL
  const getAdminProfileImage = () => {
    return adminUser?.profileImage?.url || 
           adminUser?.profileImage || 
           null;
  };

  // Get admin email
  const getAdminEmail = () => {
    return adminUser?.email || '';
  };

  return (
    <div className={styles.adminContainer}>
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className={styles.overlay}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        ${styles.sidebar} 
        ${sidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}
        ${mobileSidebarOpen ? styles.mobileSidebarOpen : ''}
      `}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <Shield className={styles.logoIcon} />
            <span>{adminPanel || 'Admin Panel'}</span>
          </div>
          {window.innerWidth < 768 && (
            <button 
              className={styles.toggleBtn}
              onClick={toggleSidebar}
            >
              <X size={20} />
            </button>
          )}
        </div>

        <nav className={styles.navMenu}>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`${styles.navItem} ${isActive(item.path) ? styles.active : ''}`}
              onClick={() => setMobileSidebarOpen(false)}
            >
              <item.icon size={20} />
              <span className={styles.navLabel}>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div className={styles.adminInfo}>
            <div className={styles.adminAvatarWrapper}>
              {getAdminProfileImage() ? (
                <img 
                  src={getAdminProfileImage()} 
                  alt={getAdminName()}
                  className={styles.adminAvatarImage}
                />
              ) : (
                <div className={styles.adminAvatar}>
                  {getAdminAvatar()}
                </div>
              )}
            </div>
            {sidebarOpen && (
              <div className={styles.adminDetails}>
                <span className={styles.adminName}>
                  {getAdminName()}
                </span>
                <span className={styles.adminRole}>
                  {administrator || 'Administrator'}
                </span>
                {getAdminEmail() && (
                  <span className={styles.adminEmail}>
                    {getAdminEmail()}
                  </span>
                )}
              </div>
            )}
          </div>
          <button 
            className={styles.logoutBtn}
            onClick={handleLogout}
          >
            <LogOut size={18} />
            {sidebarOpen && <span>{logoutLabel || 'Logout'}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        ${styles.mainContent} 
        ${sidebarOpen ? styles.mainContentOpen : styles.mainContentClosed}
      `}>
        <header className={styles.topHeader}>
          <button 
            className={styles.mobileMenuBtn}
            onClick={() => setMobileSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>
          <h1 className={styles.pageTitle}>
            {menuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
          </h1>
          <div className={styles.headerActions}>
            {/* Language Selector */}
            <div className="language-selector" style={{ position: 'relative' }}>
              <button
                className={styles.languageBtn}
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                disabled={isTranslating}
              >
                <Globe size={18} />
                <span className={styles.languageBtnText}>
                  {getCurrentLanguageLabel()}
                </span>
                <ChevronDown size={16} />
              </button>

              {showLanguageMenu && (
                <div className={styles.languageDropdown}>
                  <div className={styles.languageDropdownHeader}>
                    <span className={styles.languageDropdownTitle}>
                      {selectLanguage || 'Select Language'}
                    </span>
                    <span className={styles.languageDropdownSubtitle}>
                      {currentLanguageLabel || 'Current Language'}: {getCurrentLanguageLabel()}
                    </span>
                  </div>
                  <div className={styles.languageDropdownList}>
                    {languages.map((item) => (
                      <button
                        key={item.code}
                        className={`${styles.languageOption} ${
                          currentLanguage === item.code ? styles.languageOptionActive : ''
                        }`}
                        onClick={() => handleLanguageSelect(item.code)}
                        disabled={isTranslating}
                      >
                        <div className={styles.languageOptionLeft}>
                          <span className={styles.languageFlag}>{item.flag}</span>
                          <div className={styles.languageOptionInfo}>
                            <span className={styles.languageOptionName}>{item.name}</span>
                            <span className={styles.languageOptionCode}>
                              {item.code.toUpperCase()}
                            </span>
                          </div>
                        </div>
                        {currentLanguage === item.code && (
                          <Check size={18} color={colors.primary} />
                        )}
                      </button>
                    ))}
                  </div>
                  {isTranslating && (
                    <div className={styles.languageLoading}>
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className={styles.languageLoadingText}>
                        {updatingLanguage || 'Updating language...'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Notification Bell - Removed pending count */}
            <div 
              className="notification-dropdown" 
              style={{ position: 'relative' }}
            >
              <button 
                className={styles.notificationBtn}
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} />
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className={styles.notificationDropdown}>
                  <div className={styles.notificationDropdownHeader}>
                    <span className={styles.notificationDropdownTitle}>
                      Notifications
                    </span>
                  </div>
                  <div className={styles.notificationDropdownList}>
                    <div className={styles.notificationEmpty}>
                      <p>{noNotifications || 'No new notifications'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.headerSearch}> 
              <Search size={18} />
              <input type="text" placeholder={searchPlaceholder || 'Search...'} />
            </div>
          </div>
        </header>

        <div className={styles.pageContent}>
          <Routes>
            <Route path="/" element={<AdminOverview />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="applications" element={<AdminProviderApplications />} />
              <Route path="messages" element={<AdminMessagesPage />} /> {/* ← Add this */}
            <Route path="reports" element={<AdminReports />} />
            <Route path="donations" element={<AdminDonations />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;